import type { TFunction } from 'i18next';

/** Human-readable byte size (e.g. 184320 → "180.0 KB"). */
export function formatBytes(bytes: number | null | undefined): string {
  if (bytes == null) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1_048_576) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / 1_048_576).toFixed(1)} MB`;
}

/**
 * Compact interview duration from whole seconds (3720 → "1h 2m"). The units come
 * from i18n rather than the template, so Arabic reads in Arabic. A positive but
 * sub-minute total says "<1m", never "0m" — recorded time that renders as zero
 * reads as a broken timer.
 */
export function formatDuration(seconds: number, t: TFunction): string {
  const total = Number.isFinite(seconds) && seconds > 0 ? Math.floor(seconds / 60) : 0;
  if (total < 1) return seconds > 0 ? t('projects.durationUnderMinute') : t('projects.durationNone');
  const hours = Math.floor(total / 60);
  return hours < 1
    ? t('projects.durationMinutes', { minutes: total })
    : t('projects.durationHours', { hours, minutes: total % 60 });
}

/** Truncate a string to a max length with an ellipsis. */
export function truncate(value: string, max: number): string {
  return value.length > max ? `${value.slice(0, max)}…` : value;
}

/**
 * Drop a separator the generator left at the front of a stored summary.
 *
 * The AI service splits the card-sized brief off the rollup at a `## BRIEF`
 * marker and only trims whitespace, so a model that writes `## BRIEF: …` stores
 * the colon as the first character of the sentence. Rows written that way are
 * never regenerated, so the reader has to be protected here rather than upstream.
 */
export function stripLeadingSeparator(value: string): string {
  return value.replace(/^[\s:：\-–—‑·•]+/u, '');
}

/**
 * A timestamp as a locale-aware date.
 *
 * Deliberately absolute rather than "3 days ago": these stamps mark when work
 * happened, and a relative label forces the reader to do arithmetic to compare
 * two of them. `Intl` handles the calendar and the numerals, so Arabic reads in
 * Arabic without a second code path.
 */
export function formatDate(iso: string | null | undefined, locale: string): string {
  if (!iso) return '';
  const parsed = new Date(iso);
  if (Number.isNaN(parsed.getTime())) return '';
  return new Intl.DateTimeFormat(locale, { dateStyle: 'medium' }).format(parsed);
}

/** A moment, not a day. A receipt for something irreversible has to say when it
 *  happened precisely enough that a reviewer can match it to what they remember
 *  doing — "Aug 14" is not that. */
export function formatDateTime(iso: string | null | undefined, locale: string): string {
  if (!iso) return '';
  const parsed = new Date(iso);
  if (Number.isNaN(parsed.getTime())) return '';
  return new Intl.DateTimeFormat(locale, { dateStyle: 'medium', timeStyle: 'short' }).format(parsed);
}

/** The clock without the calendar.
 *
 *  For a run of stamps that all fall under one day already named above them — a
 *  conversation's turns, most of which are minutes apart. Repeating "Aug 24, 2026"
 *  on every line does not tell a reader anything the line before it did not, and at
 *  eight turns it is the widest thing in the transcript. */
export function formatTime(iso: string | null | undefined, locale: string): string {
  if (!iso) return '';
  const parsed = new Date(iso);
  if (Number.isNaN(parsed.getTime())) return '';
  return new Intl.DateTimeFormat(locale, { timeStyle: 'short' }).format(parsed);
}
